import z from "zod";
import { mapErrorsToFields } from "../../../../utils";
import ShoppingCompanyService from "../../../../services/ShoppingCompanyService";

const api = new ShoppingCompanyService();
const maxLogoBytes = 8 * 1024 * 1024;
const allowedLogoTypes = ["image/jpeg", "image/png", "image/webp"];

export type State = {
  message: string | null;
  errors?: {
    name?: string[];
    url?: string[];
    discountCode?: string[];
    logo?: string[];
    general?: string[];
  };
  success: boolean;
  defaultValues: {
    id?: string;
    name?: string;
    url?: string;
    discountCode?: string;
    logo?: string;
  };
};

const FormSchema = z.object({
  name: z
    .string()
    .min(1, { message: "Name is required." })
    .max(200, { message: "Name cannot exceed 200 characters." }),
  url: z
    .string()
    .max(500, { message: "Url cannot exceed 500 characters." })
    .refine((value) => !value || /^https?:\/\//i.test(value), {
      message: "Url must be a valid http or https address.",
    })
    .optional(),
  discountCode: z
    .string()
    .max(100, { message: "Discount code cannot exceed 100 characters." })
    .optional(),
});

function parseFormData(formData: FormData) {
  return {
    name: formData.get("name")?.toString() || "",
    url: formData.get("url")?.toString() || "",
    discountCode: formData.get("discountCode")?.toString() || "",
    logo: formData.get("logo")?.toString() || "",
  };
}

function readLogoFile(formData: FormData) {
  const logo = formData.get("logoFile");
  if (!(logo instanceof File) || logo.size === 0) return null;
  return logo;
}

function validateLogo(file: File | null): string | null {
  if (!file) return null;
  if (!allowedLogoTypes.includes(file.type)) {
    return "Only JPEG, PNG, and WebP images are allowed.";
  }
  if (file.size > maxLogoBytes) {
    return "Image must be 8 MB or smaller.";
  }
  return null;
}

function apiMessage(error: any, fallback: string) {
  return (
    error?.response?.data?.message ??
    error?.response?.data?.description ??
    error?.response?.data?.Message ??
    fallback
  );
}

export async function createShoppingCompany(
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = parseFormData(formData);
  const logoFile = readLogoFile(formData);

  try {
    const validatedFields = FormSchema.safeParse({
      name: defaultValues.name,
      url: defaultValues.url || undefined,
      discountCode: defaultValues.discountCode,
    });
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to create shopping company.",
        defaultValues,
        success: false,
      };
    }

    const logoError = validateLogo(logoFile);
    if (logoError) {
      return {
        errors: { logo: [logoError] },
        message: "Failed to create shopping company.",
        defaultValues,
        success: false,
      };
    }

    const result = await api.createShoppingCompany({
      name: validatedFields.data.name,
      url: validatedFields.data.url || null,
      discountCode: validatedFields.data.discountCode,
      logo: null,
    });

    if (result.status === 201 && logoFile) {
      const createdId = result.data?.id as string | undefined;
      if (!createdId) {
        return {
          errors: {},
          message: "Shopping company was created, but the logo could not be uploaded.",
          defaultValues,
          success: false,
        };
      }

      await api.uploadLogo(createdId, logoFile);
    }

    if (result.status === 201) {
      return {
        errors: {},
        message: "Shopping company created successfully.",
        defaultValues: {
          id: result.data?.id,
          ...defaultValues,
        },
        success: true,
      };
    }
  } catch (error) {
    console.error("Error creating shopping company:", error);
    return {
      errors: { general: [apiMessage(error, "Failed to create shopping company.")] },
      message: apiMessage(error, "Failed to create shopping company."),
      defaultValues,
      success: false,
    };
  }

  return {
    errors: {},
    message: "Failed to create shopping company.",
    defaultValues,
    success: false,
  };
}

export async function updateShoppingCompany(
  id: string,
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = parseFormData(formData);
  const logoFile = readLogoFile(formData);

  try {
    const validatedFields = FormSchema.safeParse({
      name: defaultValues.name,
      url: defaultValues.url || undefined,
      discountCode: defaultValues.discountCode,
    });
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to update shopping company.",
        defaultValues,
        success: false,
      };
    }

    const logoError = validateLogo(logoFile);
    if (logoError) {
      return {
        errors: { logo: [logoError] },
        message: "Failed to update shopping company.",
        defaultValues,
        success: false,
      };
    }

    const result = await api.updateShoppingCompany({
      id,
      name: validatedFields.data.name,
      url: validatedFields.data.url || null,
      discountCode: validatedFields.data.discountCode,
      logo: defaultValues.logo || null,
    });

    if (result.status === 204 && logoFile) {
      await api.uploadLogo(id, logoFile);
    }

    if (result.status === 204) {
      return {
        errors: {},
        message: "Shopping company updated successfully.",
        defaultValues,
        success: true,
      };
    }
  } catch (error) {
    console.error("Error updating shopping company:", error);
    return {
      errors: { general: [apiMessage(error, "Failed to update shopping company.")] },
      message: apiMessage(error, "Failed to update shopping company."),
      defaultValues,
      success: false,
    };
  }

  return {
    errors: {},
    message: "Failed to update shopping company.",
    defaultValues,
    success: false,
  };
}

export async function deleteShoppingCompany(
  id: string
): Promise<{ success: boolean }> {
  try {
    const result = await api.deleteShoppingCompany(id);
    return { success: result.status === 204 };
  } catch (error) {
    console.error("Error deleting shopping company:", error);
  }

  return { success: false };
}
